import { Injectable } from '@angular/core';
import { floorDecimal } from '../shared/utils/date.util';
import { DeviceMeasurementDto, MeasurementDto } from '../types/dtos/device.dto';
import { TimeRangeKey } from '../types/interfaces/charts.interface';

@Injectable({
  providedIn: 'root',
})
export class DiagramService {

  public generateTimeSeriesData(
    fromDate: Date,
    toDate: Date,
    type: TimeRangeKey,
    data: DeviceMeasurementDto[]
  ): {
    labels: string[],
    data: number[]
  } {
    let timeDateKeys: Date[] = this._generateTimeDateKeys(fromDate, toDate, type);
    let measurements: number[] = this._generateBucketKeys(timeDateKeys, data, type);

    return {
      labels: this._convertDatesToTimeLabel(timeDateKeys, type),
      data: measurements
    }
  }

  public calcDeltaValues(data: number[]): number[] {
    const deltaValues: number[] = [0];

    for (let i = 1; i < data.length; i++) {
      deltaValues.push(floorDecimal(data[i] - data[i - 1], 4));
    }

    return deltaValues;
  }

  private _generateTimeDateKeys(
    fromDate: Date,
    toDate: Date,
    type: TimeRangeKey,
  ): Date[] {
    const dateKeys: Date[] = [];

    const current: Date = this.roundDateToBucketStart(fromDate, type);

    while (current <= toDate) {
      dateKeys.push(new Date(current.getTime()));

      if (type === '1H') {
        current.setMinutes(current.getMinutes() + 5);
      } else if (type === '1D') {
        current.setHours(current.getHours() + 1);
      } else {
        current.setDate(current.getDate() + 1);
      }
    }

    return dateKeys;
  }

  private _convertDatesToTimeLabel(dateKeys: Date[], type: TimeRangeKey): string[] {
    return dateKeys.map(date => this._getBucketLabel(date, type));
  }

  private _generateBucketKeys(
    dateKeys: Date[],
    measurementData: DeviceMeasurementDto[],
    type: TimeRangeKey
  ): number[] {
    const measurements: MeasurementDto[] = measurementData
      .map(measurement => ({
        time: new Date(measurement.time),
        value: Number(measurement.value),
      }))
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    const data: number[] = [];
    let index: number = 0;
    let lastMeasurement: MeasurementDto | null = null;

    for (let i = 0; i < dateKeys.length; i++) {
      let dateKey = dateKeys[i];
      if (type === '1H') { dateKey.setMinutes(dateKey.getMinutes() + 5, 0, 0) }
      else if (type === '1D') { dateKey.setMinutes(0, 0); }
      else { dateKey.setHours(0, 0, 0); }

      // if last value
      if (i === dateKeys.length - 1){
        dateKey = new Date();
      }

      while (
        index < measurements.length &&
        measurements[index].time.getTime() <= dateKey.getTime()
      ) {
        lastMeasurement = measurements[index];
        index++;
      }

      data.push(
        lastMeasurement
          ? floorDecimal(lastMeasurement.value / 1000, 4)
          : 0
      );
    }

    return data;
  }

  public roundDateToBucketStart(date: Date, type: TimeRangeKey): Date {
    switch (type) {
      case '1H':
        const minutes: number = date.getMinutes();
        const roundedMinutes: number = Math.floor(minutes / 5) * 5;

        date.setMinutes(roundedMinutes, 0, 0);
        break
      case '1D':
        date.setMinutes(0, 0, 0);
        break
      default:
        date.setHours(0, 0, 0, 0);
        break
    }

    return date
  }

  private _getBucketLabel(date: Date, type: TimeRangeKey): string {
    switch (type) {
      case '1H':
        return date.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
        });
      case '1D':
        return date.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
        });
      default:
        return date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
        });
    }
  }
}